// src/components/analytics/EfficiencyAnalysis.js
import React from 'react';
import { 
  Box, Paper, Typography, Grid, Card, CardContent, 
  LinearProgress, Chip, Alert 
} from '@mui/material';
import { Speed, TrendingUp, Warning } from '@mui/icons-material';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer
} from 'recharts';

const EfficiencyAnalysis = ({ kpis, analysis, t }) => {
  // Дані для радарного графіка
  const radarData = [
    { subject: t('analytics_production_volume'), A: Math.min(kpis.planProgress / 100, 1), fullMark: 1 },
    { subject: t('analytics_resource_usage'), A: 0.7, fullMark: 1 },
    { subject: t('analytics_quality'), A: 0.8, fullMark: 1 },
    { subject: t('analytics_efficiency'), A: analysis.overallEfficiency / 100, fullMark: 1 },
    { subject: t('analytics_plan_adherence'), A: Math.min(kpis.planProgress / 100, 1), fullMark: 1 },
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Speed color="primary" />
        <Typography variant="h6">
          {t('analytics_efficiency_analysis')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Радарний графік */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 300 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('analytics_performance_radar')}
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 1]} />
                  <Radar
                    name="Efficiency"
                    dataKey="A"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Ключові показники */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('analytics_key_metrics')}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{t('analytics_plan_completion')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {Math.round(kpis.planProgress)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={kpis.planProgress}
                  color={kpis.planProgress >= 80 ? 'success' : kpis.planProgress >= 60 ? 'warning' : 'error'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{t('analytics_resource_efficiency')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {Math.round(analysis.overallEfficiency)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={analysis.overallEfficiency}
                  color={analysis.overallEfficiency >= 80 ? 'success' : analysis.overallEfficiency >= 60 ? 'warning' : 'error'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{t('analytics_tunnel_utilization')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {Math.round((kpis.totalTunnel / (kpis.planTarget || 1)) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(kpis.totalTunnel / (kpis.planTarget || 1)) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Тренди */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('analytics_trends_analysis')}
              </Typography>
              <Grid container spacing={2}>
                {analysis.trends.map((trend, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp 
                        color={trend.type === 'growth' ? 'success' : trend.type === 'decline' ? 'error' : 'disabled'}
                        sx={{ 
                          transform: trend.type === 'decline' ? 'rotate(180deg)' : 'none' 
                        }}
                      />
                      <Box>
                        <Typography variant="body2">
                          {t(trend.message)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(trend.percentage)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EfficiencyAnalysis;